import { X } from "lucide-react";
import { useAdminUsers } from "../hooks/useAdminUsers";

export function UsersPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { users, loading, refreshing, error, reload } = useAdminUsers(open, open ? "users-panel" : undefined);
  const busy = loading || refreshing;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-black/20"
        aria-label="Close users panel"
        onClick={onClose}
      />
      <aside className="relative flex h-full w-full max-w-md flex-col border-l border-border-subtle bg-card shadow-lg">
        <header className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
          <h2
            className="font-semibold text-text-primary"
            style={{ fontSize: "var(--font-heading)" }}
          >
            Users
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-text-secondary hover:bg-page hover:text-text-primary"
            aria-label="Close"
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4">
          {loading && users.length === 0 && (
            <p className="text-text-secondary" style={{ fontSize: "var(--font-body)" }}>
              Loading…
            </p>
          )}
          {error && (
            <p className="text-status-error" style={{ fontSize: "var(--font-body)" }}>
              {error}
            </p>
          )}
          {!busy && !error && users.length === 0 && (
            <p className="text-text-secondary" style={{ fontSize: "var(--font-body)" }}>
              No users yet.
            </p>
          )}
          {!error && users.length > 0 && (
            <ul className="space-y-2">
              {users.map((u) => (
                <li
                  key={u.id}
                  className="rounded-lg border border-border-subtle px-3 py-2.5"
                >
                  <div
                    className="font-medium text-text-primary"
                    style={{ fontSize: "var(--font-body)" }}
                  >
                    {u.login}
                  </div>
                  <div
                    className="mt-0.5 text-text-secondary"
                    style={{ fontSize: "var(--font-micro)" }}
                  >
                    {u.roles.length ? u.roles.join(", ") : "no roles"}
                    {u.disabled ? " · disabled" : ""}
                    {u.email && !u.email_verified ? " · unverified" : ""}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="border-t border-border-subtle px-4 py-3">
          <button
            type="button"
            onClick={() => void reload()}
            disabled={busy}
            className="text-accent hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            style={{ fontSize: "var(--font-micro)" }}
          >
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
          {!busy && !error && (
            <span
              className="ml-3 text-text-secondary"
              style={{ fontSize: "var(--font-micro)" }}
            >
              {users.length} account{users.length === 1 ? "" : "s"}
            </span>
          )}
        </footer>
      </aside>
    </div>
  );
}
